'use client';

import { InverseHeader } from '@jouzen/ecom-components/layout';
import { BREAKPOINTS } from '@jouzen/ecom-components/v3/consts/breakpoints';
import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import {
  SwipeableProgressRatio,
  SwipeableProvider,
  SwipeableSlider,
} from '@jouzen/ecom-components/v3/SwipeableSlider';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import BackdropBlur from '@/app/components/BackdropBlur';
import Motion from '@/app/components/Motion';
import NavButtons from '@/app/components/SwipeableNavButtons';
import useAuroraRollout from '@/app/hooks/useAuroraRollout';

import { CARD_CONFIGS } from './data';

interface CardProps {
  readonly tagKey: string;
  readonly mainImageSrc: string;
  readonly mainImageAlt: string;
  readonly appUiImageSrc: string;
}

const DesktopCard = ({
  tagKey,
  mainImageSrc,
  mainImageAlt,
  appUiImageSrc,
}: CardProps) => {
  const t = useTranslations();
  const sizes = `(min-width: ${BREAKPOINTS.large}px) 33vw, 50vw`;
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  return (
    <li>
      <div
        className="relative flex h-full flex-1 flex-col gap-y-4"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="relative h-full">
          <div className="absolute z-99 mt-4 ml-6 inline-flex px-3 py-2">
            <BackdropBlur variant="pill" />
            <Typography
              Element="span"
              color="light"
              variant="eyebrow-large"
              className="relative"
            >
              {t(tagKey)}
            </Typography>
          </div>
          <Image
            className="rounded-xl object-cover"
            alt={t(mainImageAlt)}
            src={mainImageSrc}
            fill
            sizes={sizes}
          />
        </div>
        <Motion
          animate={isHovered ? 'hover' : 'initial'}
          transition={{ duration: 0.5, ease: 'easeInOut', delay: 0.2 }}
          variants={{
            initial: {
              height: 0,
              maxHeight: 0,
            },
            hover: {
              height: 'auto',
              maxHeight: '1000px',
              marginBottom: '1rem',
            },
          }}
        >
          <Image
            alt=""
            className="rounded-xl object-cover"
            src={appUiImageSrc}
            sizes={sizes}
            width={700}
            height={600}
          />
        </Motion>
      </div>
    </li>
  );
};

const MobileCard = ({
  mainImageSrc,
  mainImageAlt,
  appUiImageSrc,
  tagKey,
}: CardProps) => {
  const t = useTranslations();
  return (
    <div className="relative h-[40rem] md:h-[55rem]">
      <div className="relative h-[35rem] w-screen md:h-[45rem]">
        <div className="absolute z-99 mt-4 ml-6 inline-flex px-3 py-2">
          <BackdropBlur variant="pill" />
          <Typography
            Element="span"
            color="light"
            variant="eyebrow-large"
            className="relative"
          >
            {t(tagKey)}
          </Typography>
        </div>
        <Image
          className="rounded-xl object-cover"
          alt={t(mainImageAlt)}
          src={mainImageSrc}
          fill
          sizes="50vw"
        />
      </div>
      <Motion
        className="absolute bottom-0 flex w-full justify-center"
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1, type: 'tween', delay: 0.5 }}
      >
        <Image
          alt=""
          className="rounded-xl object-cover"
          src={appUiImageSrc}
          width={300}
          height={265}
          sizes="30vw"
        />
      </Motion>
    </div>
  );
};

const MobileCards = ({
  isAuroraEnabled,
}: {
  readonly isAuroraEnabled: boolean;
}) => {
  const t = useTranslations();
  return (
    <SwipeableProvider className="block lg:hidden">
      <div className="flex flex-col-reverse">
        <LayoutGrid>
          <GridItem colEnd={{ sm: 'full' }} colStart={{ sm: 'main' }}>
            <div className="relative mr-6 flex items-center justify-between">
              <SwipeableProgressRatio color="dark" className="flex-1" />
              <NavButtons />
            </div>
          </GridItem>
        </LayoutGrid>
        <SwipeableSlider className="pr-6">
          {CARD_CONFIGS.map((card) => (
            <MobileCard
              key={card.tagKey}
              tagKey={card.tagKey}
              mainImageSrc={card.mainImageSrc}
              mainImageAlt={card.mainImageAlt}
              appUiImageSrc={t(
                isAuroraEnabled ? card.auroraUiKey : card.legacyUiKey,
              )}
            />
          ))}
        </SwipeableSlider>
      </div>
    </SwipeableProvider>
  );
};

const DesktopCards = ({
  isAuroraEnabled,
}: {
  readonly isAuroraEnabled: boolean;
}) => {
  const t = useTranslations();
  return (
    <ul className="mt-5 hidden h-[50rem] gap-x-4 lg:flex">
      {CARD_CONFIGS.map((card) => (
        <DesktopCard
          key={card.tagKey}
          tagKey={card.tagKey}
          mainImageSrc={card.mainImageSrc}
          mainImageAlt={card.mainImageAlt}
          appUiImageSrc={t(
            isAuroraEnabled ? card.auroraUiKey : card.legacyUiKey,
          )}
        />
      ))}
    </ul>
  );
};

const ExpandableCards = () => {
  // TODO: GROW-2891 Clean up aurora rollout
  const isAuroraEnabled = useAuroraRollout();

  return (
    <InverseHeader enter>
      <Motion
        initial={{ filter: 'blur(5px)', opacity: 0 }}
        whileInView={{ filter: 'blur(0px)', opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1, delay: 0.5 }}
        data-cy="why-oura-wellness-cards"
        aria-hidden // hidden from screen readers because it is all visual animations of images
      >
        <MobileCards isAuroraEnabled={isAuroraEnabled} />
        <DesktopCards isAuroraEnabled={isAuroraEnabled} />
      </Motion>
    </InverseHeader>
  );
};

export default ExpandableCards;
